import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, Paper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import CertificateForm from '../components/CertificateForm';
import LoadingSpinner from '../components/LoadingSpinner';
import MechanicalValuesDisplay from '../components/MechanicalValuesDisplay';
import MetalValuesDisplay from '../components/MetalValuesDisplay';
import { apiService } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { Certificate } from '../types';
import { downloadBlob } from '../utils/fileUtils';
import { tokens } from '../theme';
import { countryLabel } from '../components/CountrySelect';
import { zollProofIncomplete } from '../components/ZollIncompleteMark';
import { useToast } from '../components/ToastProvider';
import { apiErrorMessage } from '../utils/apiError';

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 500 }}>
        {children}
      </Typography>
    </Box>
  );
}

const CertificateDetailPage: React.FC = () => {
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toastError } = useToast();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (id) {
      loadCertificate(parseInt(id, 10));
    }
  }, [id]);

  const loadCertificate = async (certificateId: number) => {
    try {
      const data = await apiService.getCertificate(certificateId);
      setCertificate(data);
      setError(null);
    } catch (err) {
      setError('Lieferanten Zertifikat nicht gefunden');
      console.error('Error loading certificate:', err);
    } finally {
      setLoading(false);
    }
  };

  async function handleDownloadPdf(): Promise<void> {
    if (!certificate) {
      return;
    }
    try {
      const blob = await apiService.downloadCertificatePdf(certificate.id);
      const filename = certificate.pdf_filename || `zertifikat-${certificate.id}.pdf`;
      downloadBlob(blob, filename);
    } catch (err) {
      toastError(apiErrorMessage(err, 'Fehler beim Herunterladen der PDF'));
      console.error('Error downloading PDF:', err);
    }
  }

  const canEdit =
    certificate &&
    (user?.role === 'KALOTTE' || (user?.role === 'RUBY' && certificate.business_unit === 'RUBY'));

  if (loading) {
    return <LoadingSpinner message="Lieferanten Zertifikat wird geladen..." />;
  }

  if (error || !certificate) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/certificates')} sx={{ mb: 2 }}>
          Zurück zu Lieferanten Zertifikaten
        </Button>
        <Alert severity="error">{error || 'Lieferanten Zertifikat nicht gefunden'}</Alert>
      </Box>
    );
  }

  const metaLine = [
    certificate.lieferant
      ? `${certificate.lieferant.name} (${certificate.lieferant.short_name})`
      : null,
    certificate.material_grade.name,
    certificate.surface?.name,
    certificate.business_unit === 'RUBY' ? 'Ruby' : 'Kalotte',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box>
      <Button onClick={() => navigate('/certificates')} startIcon={<ArrowBackIcon />} sx={{ mb: 1 }}>
        Zertifikate
      </Button>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">
            {certificate.certificate_number}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: 13 }}>
            {metaLine}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {certificate.pdf_filename && (
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleDownloadPdf}>
              PDF
            </Button>
          )}
          {canEdit && (
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditDialogOpen(true)}>
              Bearbeiten
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {certificate.blocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Dieses Lieferanten Zertifikat ist noch unvollständig. Eine Charge kann schon angelegt
          werden. Ein Zeugnis darf erst nach dem vollständigen Speichern gedruckt werden.
        </Alert>
      )}

      {zollProofIncomplete(certificate) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Für ein Zollzeugnis fehlen Schmelzland oder Schmelznummer.
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        <Meta label="Datum">{dayjs(certificate.certificate_date).format('DD.MM.YYYY')}</Meta>
        <Meta label="Schmelznummer">{certificate.heat_number || '—'}</Meta>
        <Meta label="Schmelzland">{certificate.melt_country ? countryLabel(certificate.melt_country, true) : '—'}</Meta>
        <Meta label="Schmelzwerk">{certificate.melt_works || '—'}</Meta>
        <Meta label="Ursprung">{certificate.country ? countryLabel(certificate.country) : '—'}</Meta>
        <Meta label="Norm">{certificate.material_norm?.name ?? '—'}</Meta>
        <Meta label="Erstellt von">{certificate.creator.full_name}</Meta>
        {certificate.notes && (
          <Box sx={{ gridColumn: '1 / -1', pt: 1, borderTop: `1px solid ${tokens.border}` }}>
            <Meta label="Notizen">{certificate.notes}</Meta>
          </Box>
        )}
      </Paper>

      <MetalValuesDisplay
        metalValues={certificate.metal_values || []}
        materialGradeCompositions={certificate.material_grade.metal_compositions || []}
      />
      <MechanicalValuesDisplay
        mechanicalValues={certificate.mechanical_values || []}
        materialGradeMechanicalProperties={certificate.material_grade.mechanical_properties || []}
      />

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle>Lieferanten Zertifikat bearbeiten</DialogTitle>
        <DialogContent>
          <CertificateForm
            certificate={certificate}
            onSuccess={() => {
              setEditDialogOpen(false);
              loadCertificate(certificate.id);
            }}
            onCancel={() => setEditDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CertificateDetailPage;
